/**
 * Typed registry of every Statsig gate the OS frontend reads. Keeping the
 * canonical names in one place stops typos at call sites and makes it cheap
 * to grep for "where is this gate used" across the codebase.
 *
 * Gate names mirror the Java side (`FeatureGate` enum in the control-plane)
 * so the same name works in both worlds and the Statsig dashboard only has
 * one source of truth per flag.
 *
 * If you add a flag here, also add it to the backend `FeatureGate` enum
 * (clientFacing=true) and the gov profile's `controlplane.feature-flags.gates`
 * — GovCloud has no Statsig and reads client gates from the backend config
 * provider via `GET /api/v1/feature-flags`.
 */
export const FeatureGate = {
  /** Gates the post-payment lead-qualification onboarding form (AIB-6641). */
  OS_ONBOARDING_FORM: 'os_onboarding_form',
  /**
   * Gates the new Processes page. When OFF the `/processes` route falls
   * back to a redirect home and the sidebar entry is hidden, exactly as
   * if the page didn't exist. Lets us roll the page out incrementally as
   * subsequent PRs land the actual UI.
   */
  OS_PROCESSES_PAGE: 'processes_page',
  /**
   * Gates the Custom Tool Workbench (Settings → Tools). ON only in GovCloud;
   * OFF everywhere else, so the tab, route, and backend endpoints stay hidden.
   */
  CUSTOM_TOOL_WORKBENCH: 'custom_tool_workbench',
  /**
   * Gates the AI assistant panel in the Processes editor. Also enforced
   * server-side, so flipping it off stops the panel mounting *and* refuses
   * any message sent to an already-open one.
   */
  PROCESS_ASSISTANT: 'process_assistant',
  /**
   * Gates Apps — the guided single-purpose workflows. When OFF the sidebar
   * entry is hidden, the `/apps` routes redirect home, and the backend
   * answers 404, so the feature is absent rather than merely invisible.
   */
  OS_APPS: 'os_apps',
  /**
   * Gates the App Builder: the "My apps" and org-shared gallery sections and
   * the builder page. Only meaningful alongside OS_APPS; the backend answers
   * 404 and lists built-in apps alone while it is OFF.
   */
  OS_APP_BUILDER: 'os_app_builder',
  /**
   * Gates HTML views in Apps (an app's own page, and HTML a run presents),
   * HTML artifacts in chat, and the screenshot sent with app and App Builder
   * turns. OFF in GovCloud: a `srcdoc` frame inherits the page's CSP, and
   * Caddy's `script-src 'self'` there would block every page's inline script.
   */
  OS_APP_HTML_VIEW: 'os_app_html_view',
  /**
   * Gates public links to user apps. The backend evaluates it as the app's
   * owner on every public read, so turning it off kills every link at once.
   */
  OS_APP_PUBLIC_LINKS: 'os_app_public_links',
} as const;

export type FeatureGateName = (typeof FeatureGate)[keyof typeof FeatureGate];
